import { FunctionComponent, MouseEvent, useState } from "react";
import { ButtonBase, CircularProgress, ListItemIcon, Menu, MenuItem } from "@mui/material";
import { Check, KeyboardArrowDown } from "@mui/icons-material";

import { PRIMARY_COLOR } from "../../utils";

type YearLabelProps = {
    year: number,
    fontSize: number,
}

/** Century in the text color, the rest in yellow – like "Moro" + "Kilometry" in the title. */
const YearLabel: FunctionComponent<YearLabelProps> = ({ year, fontSize }) => {
    const text = `${year}`;

    return (
        <span className="year-label" style={{ fontSize }}>
            {text.slice(0, 2)}
            <span style={{ color: PRIMARY_COLOR }}>{text.slice(2)}</span>
        </span>
    );
};

export type YearSelectProps = {
    year: number,
    /** Years with results, newest first */
    years: number[],
    /** Shows a spinner instead of the arrow while the selected year loads */
    isLoading?: boolean,
    onChange: (year: number) => void,
    onOpen?: () => void,
}

export const YearSelect: FunctionComponent<YearSelectProps> = ({ year, years, isLoading = false, onChange, onOpen }) => {
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
    const isOpened = anchorElement !== null;
    const latestYear = years[0];

    const open = (event: MouseEvent<HTMLElement>) => {
        setAnchorElement(event.currentTarget);
        onOpen?.();
    };

    const close = () => setAnchorElement(null);

    const select = (value: number) => {
        close();
        if (value !== year) {
            onChange(value);
        }
    };

    return (
        <>
            <ButtonBase
                onClick={open}
                aria-haspopup="listbox"
                aria-expanded={isOpened}
                aria-label={`Ročník ${year}, změnit`}
                aria-busy={isLoading}
                sx={{ alignSelf: "center", gap: 0.75, height: 44, px: 1.5, borderRadius: 999 }}
            >
                <YearLabel year={year} fontSize={20}/>
                {isLoading ? (
                    // 18 px + 3 px margins keep the 24 px box of the arrow, so nothing shifts.
                    <CircularProgress size={18} thickness={5} sx={{ color: "inherit", mx: "3px" }}/>
                ) : (
                    <KeyboardArrowDown sx={{ transition: "transform 200ms", transform: isOpened ? "rotate(180deg)" : "none" }}/>
                )}
            </ButtonBase>
            <Menu
                anchorEl={anchorElement}
                open={isOpened}
                onClose={close}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                MenuListProps={{ role: "listbox" }}
            >
                {years.map((value) => (
                    <MenuItem
                        key={value}
                        role="option"
                        selected={value === year}
                        aria-selected={value === year}
                        onClick={() => select(value)}
                        sx={{
                            minWidth: 200,
                            minHeight: 44,
                            gap: 1,
                            // Neutral highlight – the default yellow tint would swallow the yellow digits.
                            "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "action.selected" },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 24 }}>
                            {value === year ? <Check fontSize="small"/> : null}
                        </ListItemIcon>
                        <YearLabel year={value} fontSize={16}/>
                        <span style={{ flex: 1 }}/>
                        {value === latestYear ? <span style={{ fontSize: 12, opacity: 0.5 }}>aktuální</span> : null}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
